import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaGripVertical,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import {
  FLOW_COMPONENT_GROUPS,
  ITEM_MIME,
  PALETTE_MIME,
  createFlowComponent,
  createFlowScreen,
  optionsToText,
  parseFlowJsonToScreens,
  stringifyFlowScreens,
  textToOptions,
} from "../../utils/whatsappFlowJson";

const INPUT_TYPES = ["text", "email", "number", "phone", "password"];

const WhatsAppFlowBuilder = ({ value, onChange }) => {
  const [screens, setScreens] = useState(() => parseFlowJsonToScreens(value));
  const [screenId, setScreenId] = useState(() => screens[0]?.id || "");
  const [selectedId, setSelectedId] = useState("");
  const [dropIndex, setDropIndex] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const activeScreen = useMemo(
    () => screens.find((screen) => screen.id === screenId) || screens[0],
    [screens, screenId],
  );
  const selected = useMemo(
    () =>
      (activeScreen?.components || []).find((item) => item._id === selectedId) ||
      null,
    [activeScreen, selectedId],
  );

  useEffect(() => {
    onChangeRef.current?.(stringifyFlowScreens(screens));
  }, [screens]);

  const updateScreens = (next) => {
    setScreens(next);
  };

  const patchScreen = (id, patch) => {
    updateScreens(
      screens.map((screen) =>
        screen.id === id ? { ...screen, ...patch } : screen,
      ),
    );
  };

  const patchComponent = (componentId, patch) => {
    if (!activeScreen) return;
    patchScreen(activeScreen.id, {
      components: activeScreen.components.map((item) =>
        item._id === componentId ? { ...item, ...patch } : item,
      ),
    });
  };

  const insertComponent = (type, index) => {
    if (!activeScreen) return;
    const created = createFlowComponent(type);
    const current = [...activeScreen.components];
    const withoutFooter =
      type === "Footer"
        ? current.filter((item) => item.type !== "Footer")
        : current;
    const footerIndex = withoutFooter.findIndex((item) => item.type === "Footer");
    let insertAt = index == null ? withoutFooter.length : index;
    if (type === "Footer") {
      withoutFooter.push(created);
    } else {
      if (footerIndex >= 0 && insertAt > footerIndex) insertAt = footerIndex;
      withoutFooter.splice(insertAt, 0, created);
    }
    patchScreen(activeScreen.id, { components: withoutFooter });
    setSelectedId(created._id);
  };

  const moveComponent = (fromId, toIndex) => {
    if (!activeScreen) return;
    const current = [...activeScreen.components];
    const fromIndex = current.findIndex((item) => item._id === fromId);
    if (fromIndex < 0) return;
    const [moved] = current.splice(fromIndex, 1);
    let insertAt = toIndex;
    if (insertAt > fromIndex) insertAt -= 1;
    current.splice(Math.max(0, insertAt), 0, moved);
    const footer = current.filter((item) => item.type === "Footer");
    const rest = current.filter((item) => item.type !== "Footer");
    patchScreen(activeScreen.id, { components: [...rest, ...footer.slice(-1)] });
  };

  const removeComponent = (componentId) => {
    if (!activeScreen) return;
    patchScreen(activeScreen.id, {
      components: activeScreen.components.filter((item) => item._id !== componentId),
    });
    if (selectedId === componentId) setSelectedId("");
  };

  const addScreen = () => {
    const next = createFlowScreen(screens.length);
    next.terminal = true;
    const updated = screens.map((screen, index) =>
      index === screens.length - 1 ? { ...screen, terminal: false } : screen,
    );
    updateScreens([...updated, next]);
    setScreenId(next.id);
    setSelectedId("");
  };

  const removeScreen = (id) => {
    if (screens.length <= 1) return;
    const next = screens.filter((screen) => screen.id !== id);
    updateScreens(next);
    if (screenId === id) setScreenId(next[0].id);
  };

  const onPaletteDragStart = (event, type) => {
    event.dataTransfer.setData(PALETTE_MIME, type);
    event.dataTransfer.setData("text/plain", `palette:${type}`);
    event.dataTransfer.effectAllowed = "copy";
  };

  const onItemDragStart = (event, id) => {
    event.dataTransfer.setData(ITEM_MIME, id);
    event.dataTransfer.setData("text/plain", `item:${id}`);
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOverSlot = (event, index) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = event.dataTransfer.effectAllowed || "copy";
    setDropIndex(index);
  };

  const onDropSlot = (event, index) => {
    event.preventDefault();
    event.stopPropagation();
    const plain = event.dataTransfer.getData("text/plain") || "";
    const type =
      event.dataTransfer.getData(PALETTE_MIME) ||
      (plain.startsWith("palette:") ? plain.slice(8) : "");
    const itemId =
      event.dataTransfer.getData(ITEM_MIME) ||
      (plain.startsWith("item:") ? plain.slice(5) : "");
    if (type) insertComponent(type, index);
    else if (itemId) moveComponent(itemId, index);
    setDropIndex(null);
  };

  const renderPreview = (item) => {
    switch (item.type) {
      case "TextHeading":
        return <h3 className="wa-flow-preview-heading">{item.text}</h3>;
      case "TextSubheading":
        return <h4 className="wa-flow-preview-sub">{item.text}</h4>;
      case "TextBody":
        return <p className="wa-flow-preview-body">{item.text}</p>;
      case "TextCaption":
        return <p className="wa-flow-preview-caption">{item.text}</p>;
      case "TextInput":
      case "TextArea":
      case "DatePicker":
        return (
          <label className="wa-flow-preview-field">
            <span>{item.label}</span>
            <span className="wa-flow-preview-control">
              {item.type === "TextArea" ? "Multiline input" : item["input-type"] || item.type}
            </span>
          </label>
        );
      case "Dropdown":
      case "RadioButtonsGroup":
      case "CheckboxGroup":
        return (
          <label className="wa-flow-preview-field">
            <span>{item.label}</span>
            <ul className="wa-flow-preview-options">
              {(item["data-source"] || []).map((opt) => (
                <li key={opt.id}>{opt.title}</li>
              ))}
            </ul>
          </label>
        );
      case "OptIn":
        return (
          <label className="wa-flow-preview-optin">
            <input type="checkbox" readOnly />
            {item.label}
          </label>
        );
      case "Image":
        return (
          <div className="wa-flow-preview-image">
            {item.src ? <img src={item.src} alt={item["alt-text"] || ""} /> : "Image"}
          </div>
        );
      case "EmbeddedLink":
        return <span className="wa-flow-preview-link">{item.text}</span>;
      case "Footer":
        return <div className="wa-flow-preview-footer">{item.label || "Continue"}</div>;
      default:
        return <span>{item.type}</span>;
    }
  };

  const renderInspector = () => {
    if (!selected) {
      return (
        <p className="wa-flow-inspector-empty">
          Select a component to edit its properties.
        </p>
      );
    }

    const set = (key, val) => patchComponent(selected._id, { [key]: val });
    const hasOptions = Array.isArray(selected["data-source"]);

    return (
      <div className="wa-flow-inspector-fields">
        <label>
          Type
          <input value={selected.type} disabled />
        </label>
        {selected.text != null && selected.type !== "EmbeddedLink" && (
          <label>
            Text
            <textarea
              rows={3}
              value={selected.text}
              onChange={(e) => set("text", e.target.value)}
            />
          </label>
        )}
        {selected.label != null && (
          <label>
            Label
            <input
              value={selected.label}
              onChange={(e) => set("label", e.target.value)}
            />
          </label>
        )}
        {selected.name != null && (
          <label>
            Field name
            <input
              value={selected.name}
              onChange={(e) => set("name", e.target.value.replace(/\s+/g, "_"))}
            />
          </label>
        )}
        {selected.required != null && (
          <label className="wa-flow-check">
            <input
              type="checkbox"
              checked={Boolean(selected.required)}
              onChange={(e) => set("required", e.target.checked)}
            />
            Required
          </label>
        )}
        {selected["input-type"] != null && (
          <label>
            Input type
            <select
              value={selected["input-type"]}
              onChange={(e) => set("input-type", e.target.value)}
            >
              {INPUT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
        )}
        {selected["helper-text"] != null && (
          <label>
            Helper text
            <input
              value={selected["helper-text"]}
              onChange={(e) => set("helper-text", e.target.value)}
            />
          </label>
        )}
        {hasOptions && (
          <label>
            Options (one per line)
            <textarea
              rows={5}
              value={optionsToText(selected["data-source"])}
              onChange={(e) => set("data-source", textToOptions(e.target.value))}
            />
          </label>
        )}
        {selected.type === "Image" && (
          <>
            <label>
              Image URL
              <input
                value={selected.src || ""}
                onChange={(e) => set("src", e.target.value)}
              />
            </label>
            <label>
              Alt text
              <input
                value={selected["alt-text"] || ""}
                onChange={(e) => set("alt-text", e.target.value)}
              />
            </label>
          </>
        )}
        {selected.type === "EmbeddedLink" && (
          <>
            <label>
              Link text
              <input
                value={selected.text || ""}
                onChange={(e) => set("text", e.target.value)}
              />
            </label>
            <label>
              URL
              <input
                value={selected["on-click-action"]?.url || ""}
                onChange={(e) =>
                  set("on-click-action", {
                    name: "open_url",
                    url: e.target.value,
                  })
                }
              />
            </label>
          </>
        )}
        {selected.type === "Footer" && (
          <p className="wa-flow-inspector-hint">
            The last screen uses Complete. Earlier screens navigate to the next
            screen automatically.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="wa-flow-builder">
      <aside className="wa-flow-palette">
        <h3>Components</h3>
        <p>Drag onto the screen</p>
        {FLOW_COMPONENT_GROUPS.map((group) => (
          <div key={group.id} className="wa-flow-palette-group">
            <strong>{group.label}</strong>
            {group.items.map((item) => (
              <button
                key={item.type}
                type="button"
                className="wa-flow-palette-item"
                draggable
                onDragStart={(event) => onPaletteDragStart(event, item.type)}
                onClick={() => insertComponent(item.type)}
              >
                {item.label}
              </button>
            ))}
          </div>
        ))}
      </aside>

      <section className="wa-flow-canvas">
        <div className="wa-flow-screens">
          {screens.map((screen) => (
            <button
              key={screen.id}
              type="button"
              className={`wa-flow-screen-tab ${
                screen.id === activeScreen?.id ? "is-active" : ""
              }`}
              onClick={() => {
                setScreenId(screen.id);
                setSelectedId("");
              }}
            >
              {screen.title || screen.id}
            </button>
          ))}
          <button type="button" className="wa-flow-screen-add" onClick={addScreen}>
            <FaPlus />
            Screen
          </button>
        </div>

        {activeScreen && (
          <div className="wa-flow-screen-meta">
            <label>
              Screen ID
              <input
                value={activeScreen.id}
                onChange={(e) => {
                  const nextId = e.target.value.toUpperCase().replace(/\s+/g, "_");
                  updateScreens(
                    screens.map((screen) =>
                      screen.id === activeScreen.id
                        ? { ...screen, id: nextId }
                        : screen,
                    ),
                  );
                  setScreenId(nextId);
                }}
              />
            </label>
            <label>
              Title
              <input
                value={activeScreen.title}
                onChange={(e) =>
                  patchScreen(activeScreen.id, { title: e.target.value })
                }
              />
            </label>
            <button
              type="button"
              className="biz-btn-secondary"
              onClick={() => removeScreen(activeScreen.id)}
              disabled={screens.length <= 1}
            >
              Remove screen
            </button>
          </div>
        )}

        <div className="wa-flow-phone">
          <div className="wa-flow-phone-bar">{activeScreen?.title || "Flow"}</div>
          <div
            className="wa-flow-phone-body"
            onDragOver={(event) =>
              onDragOverSlot(event, activeScreen?.components?.length || 0)
            }
            onDrop={(event) =>
              onDropSlot(event, activeScreen?.components?.length || 0)
            }
            onDragLeave={() => setDropIndex(null)}
          >
            {(activeScreen?.components || []).length === 0 && (
              <p className="wa-flow-empty">Drag WhatsApp components here</p>
            )}
            {(activeScreen?.components || []).map((item, index) => (
              <React.Fragment key={item._id}>
                <div
                  className={`wa-flow-drop-line ${
                    dropIndex === index ? "is-active" : ""
                  }`}
                  onDragOver={(event) => onDragOverSlot(event, index)}
                  onDrop={(event) => onDropSlot(event, index)}
                />
                <div
                  className={`wa-flow-block ${
                    selectedId === item._id ? "is-selected" : ""
                  }`}
                  draggable
                  onDragStart={(event) => onItemDragStart(event, item._id)}
                  onClick={() => setSelectedId(item._id)}
                >
                  <span className="wa-flow-block-handle" aria-hidden>
                    <FaGripVertical />
                  </span>
                  <div className="wa-flow-block-body">{renderPreview(item)}</div>
                  <button
                    type="button"
                    className="wa-flow-block-remove"
                    aria-label={`Remove ${item.type}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      removeComponent(item._id);
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <aside className="wa-flow-inspector">
        <h3>Properties</h3>
        {renderInspector()}
        <button
          type="button"
          className="wa-flow-json-toggle"
          onClick={() => setShowJson((open) => !open)}
        >
          {showJson ? "Hide JSON" : "Show JSON"}
        </button>
        {showJson && (
          <textarea
            className="wa-flow-json"
            readOnly
            rows={12}
            value={stringifyFlowScreens(screens)}
          />
        )}
      </aside>
    </div>
  );
};

export default WhatsAppFlowBuilder;
